// eslint-disable-next-line jira/filename-case
import { type ReactElement, startTransition } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { enableHydrationStatReporting } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import {
	getATLReact,
	initATLReactMetrics,
} from '@jira/platform__atlassian-react/src/atl-react.tsx';
import { setIsHydrationRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { enableStreaming } from '@jira/ssr__streaming-enabled/src/isStreamingEnabled.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { initDevHydrationReporting } from './hydration-reporting.tsx';
import { hydrationOptions } from './hydrationOptions.tsx';
import { installHydrationDebugger } from './hydration-debugger/install-debugger.tsx';

export const getIsSsrHydrationEnabled = (): boolean => {
	// We want only 1 source of truth for `clientShouldHydrate` (coming from the server), except:
	// - We keep fg('jfp-magma-force-react-hydrate-root') here for local dev convenience.
	// - Disable hydration when running on localhost:3443 (yarn start), because the front end will
	// 		almost always mismatch the server content from staging.
	if (fg('jfp-magma-force-react-hydrate-root')) {
		return true;
	}
	// Defined in JiraParcelServer
	if (typeof window !== 'undefined' && window.location.host === 'localhost:3443') {
		return false;
	}
	if (typeof window !== 'undefined' && window.SPA_STATE?.clientShouldHydrate) {
		return true;
	}
	return false;
};

export const getIsServerStreamingEnabled = (): boolean => {
	if (typeof window !== 'undefined' && window.SPA_STATE?.serverIsStreaming) {
		return true;
	}

	return false;
};

const hasAltFlag = (flagName: string): boolean => {
	try {
		if (typeof window !== 'undefined') {
			const altFlags = new URLSearchParams(window.location.search).get('alt-flags');
			return altFlags?.includes(flagName) ?? false;
		}
	} catch (e) {
		// ignore any error for alt-flags
	}
	return false;
};

export const renderReactApp = (
	children: ReactElement,
	{
		isSsrHydrationEnabled,
		isServerStreamingEnabled,
		container,
	}: {
		isSsrHydrationEnabled: boolean;
		isServerStreamingEnabled: boolean;
		/**
		 * The container to render the React app into.
		 */
		container: HTMLElement;
	},
): void => {
	initATLReactMetrics();

	if (isServerStreamingEnabled) {
		enableStreaming();
	}

	if (isSsrHydrationEnabled) {
		setIsHydrationRenderMode();
		activateReactPatches();
		if (hasAltFlag('hydration-debugger')) {
			installHydrationDebugger(container);
		}

		if (process.env.NODE_ENV === 'development') {
			// At least for now, this only has an effect in dev mode (due to our patch only being active in dev mode)
			initDevHydrationReporting(window);
		}
		enableHydrationStatReporting(container);

		// We wrap hydrateRoot in startTransition, in order to prevent state updates during hydration from breaking hydration. Instead deferring them to post-hydration.
		startTransition(() => {
			hydrateRoot(container, children, hydrationOptions);
		});

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__ = window.__HYDRATION_RECOVERABLE_ERRORS__ ?? [];
	} else {
		// If the route is not enabled for hydration but concurrent mode is enabled for this environment, render in concurrent mode
		const root = createRoot(container);
		root.render(children);
	}
};

const activateReactPatches = () => {
	const atl = getATLReact();

	atl.__ATL_SKIP_HAS_CONTEXT_CHANGED = true;
	atl.__ATL_CAPTURE_HYDRATION_ERROR_DETAILS = true;
	atl.__ATL_SYNC_HYDRATION_PRIORITY = fg('jfp_magma_jira-react-hydration-lanes');
};
